import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  newId,
  orderPlanBlocks,
  setFieldsFor,
  supersetNext,
  videoHost,
  videoUrlWithStart,
  weeksSince,
} from '@kraftwerk/shared';
import type { ProgressionSuggestion, RepRange } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { ExerciseArtFigure } from './ExerciseArt.js';
import type { LocalExercise, LocalWorkoutSession, LocalWorkoutSet } from '../../db/local.js';
import { getExercise, guidanceFor, listExercises, shortLabel } from '../../db/exercises.js';
import { listDayExercises } from '../../db/plans.js';
import {
  deleteSet,
  endSession,
  recentExerciseIds,
  lastSetsOfExercise,
  listSets,
  logSetWithBest,
  suggestionForExercise,
  swapForToday,
  updateSession,
} from '../../db/workouts.js';
import type { NewBest } from '../../db/workouts.js';
import { readProfile } from '../../db/profile.js';
import { useAchievementScope, useIsChild } from '../../hooks/useModules.js';
import { ScalePicker } from '../ScalePicker.js';
import { RestTimer } from './RestTimer.js';
import { announceRest, cancelRest, nextSetLabel, notificationsAllowed } from './restPush.js';
import type { RestPushState } from './restPush.js';

interface PlannedEntry {
  exercise: LocalExercise;
  range: RepRange;
  sets: number;
  restSeconds: number;
  planned: boolean;
  /** Nur heute getauscht: die geplante Übung, an deren Stelle diese steht. */
  swappedFrom?: LocalExercise;
  /** Supersatz-Block (23.10.2026); allein trainierte Übungen haben einen eigenen. */
  block: number;
}

/**
 * Die laufende Einheit — der Bildschirm, der im Gym in der Hand liegt.
 *
 * Alles hier kommt aus der lokalen Datenbank und geht zuerst dorthin zurück.
 * Zwischen Rack und Bank gibt es kein Netz; ein Satz, der erst zu Hause
 * gespeichert würde, wird nicht gespeichert.
 */
export function SessionScreen({
  session,
  onExit,
}: {
  session: LocalWorkoutSession;
  onExit: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const child = useIsChild();
  const [restEndsAt, setRestEndsAt] = useState<number | null>(null);
  const [restTotalMs, setRestTotalMs] = useState<number | null>(null);
  // Die beim Server angemeldete Pause — für die Mitteilung in der Tasche.
  const restId = useRef<string | null>(null);
  const [restPush, setRestPush] = useState<RestPushState>('off');
  // Im Supersatz geht es nach dem Satz ohne Pause weiter — mit wem, steht hier.
  const [supersetHint, setSupersetHint] = useState<string | null>(null);
  const [extraIds, setExtraIds] = useState<string[]>([]);
  // Beenden fragt erst „Wie lief's?" — mit einem Weg ohne Antwort.
  const [finishing, setFinishing] = useState(false);

  const sets = useLiveQuery(() => listSets(session.id, db), [session.id], [] as LocalWorkoutSet[]);

  const planned = useLiveQuery(
    async () => planFor(session),
    [session.id, session.planDayId],
    [] as PlannedEntry[],
  );

  const extras = useLiveQuery(
    async () => {
      const ids = new Set([...extraIds, ...sets.map((set) => set.exerciseId)]);
      const plannedIds = new Set(planned.map((entry) => entry.exercise.id));
      const rows: PlannedEntry[] = [];
      for (const id of ids) {
        if (plannedIds.has(id)) continue;
        const exercise = await getExercise(id, db);
        if (!exercise) continue;
        rows.push({
          exercise,
          range: { min: exercise.repMin, max: exercise.repMax },
          sets: exercise.defaultSets,
          restSeconds: exercise.restSeconds,
          planned: false,
          block: -1,
        });
      }
      return rows;
    },
    [extraIds.join(','), sets.length, planned.length],
    [] as PlannedEntry[],
  );

  // Spontan hinzugefügte Übungen stehen allein — je ein eigener Block.
  const entries = [
    ...planned,
    ...extras.map((entry, index) => ({ ...entry, block: planned.length + index })),
  ];

  // Körpergewicht wird bei der Einheit festgehalten. Fehlt es und wird es
  // gebraucht, fragt die App **einmal** danach, statt Klimmzüge stillschweigend
  // ohne Last zu verbuchen.
  const afterSet = (entry: PlannedEntry) => {
    const members = entries.filter((item) => item.block === entry.block);
    if (members.length < 2) {
      setSupersetHint(null);
      startRest(entry);
      return;
    }
    // Frisch gelesen: Der eben geloggte Satz steht in `sets` womöglich noch nicht.
    void listSets(session.id, db).then((fresh) => {
      const next = supersetNext(
        members.map((item) => ({ id: item.exercise.id, sets: item.sets })),
        doneByExercise(fresh),
        entry.exercise.id,
      );
      if (next) {
        stopRest();
        setSupersetHint(members.find((item) => item.exercise.id === next)?.exercise.name ?? null);
        return;
      }
      setSupersetHint(null);
      startRest(entry);
    });
  };

  const startRest = (entry: PlannedEntry) => {
    if (entry.restSeconds <= 0) return;
    const endsAt = Date.now() + entry.restSeconds * 1000;
    setRestEndsAt(endsAt);
    setRestTotalMs(entry.restSeconds * 1000);

    if (!notificationsAllowed()) {
      restId.current = null;
      setRestPush('off');
      return;
    }

    const id = newId();
    restId.current = id;
    setRestPush('pending');

    // Frisch gelesen: Der eben geloggte Satz steht in `sets` womöglich noch nicht.
    void listSets(session.id, db)
      .then((fresh) => {
        const done = doneByExercise(fresh);
        const label = nextSetLabel(
          entries.map((item) => ({
            exerciseId: item.exercise.id,
            name: item.exercise.name,
            sets: item.sets,
            block: item.block,
          })),
          done,
          entry.exercise.id,
        );
        return announceRest(id, endsAt, label);
      })
      .then((ok) => {
        // Inzwischen läuft schon die nächste Pause — deren Zustand gilt.
        if (restId.current === id) setRestPush(ok ? 'on' : 'failed');
      });
  };

  /** Pause beenden; läuft sie noch, wird die Mitteilung abgesagt. */
  const stopRest = () => {
    if (restId.current && restEndsAt !== null && Date.now() < restEndsAt) {
      cancelRest(restId.current);
    }
    restId.current = null;
    setRestEndsAt(null);
    setRestTotalMs(null);
  };

  const finish = (rating: number | null = null) => {
    stopRest();
    void endSession(session.id, db, rating).then(onExit);
  };

  // Ein Teen (28.10.2026) trainiert ohne Körpergewicht: Klimmzüge und Co.
  // haben dann kein Volumen in Kilogramm — eine Lücke, keine erfundene Zahl.
  const needsBodyWeight =
    !child &&
    session.bodyWeightKg === null &&
    entries.some((entry) => setFieldsFor(entry.exercise.trackingMode).bodyWeight);

  return (
    <section aria-labelledby="session-heading" className="flex flex-col gap-3">
      <div className="kw-tile flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 id="session-heading" className="truncate text-base font-semibold">
            {session.title}
          </h2>
          <p className="text-xs text-ink-muted">
            {t('training.session.startedAt', {
              time: new Date(session.startedAt).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={finishing}
          onClick={() => setFinishing((value) => !value)}
          // Bernstein gehört „Satz eintragen“ — Beenden ist nicht die nächste Handlung.
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('training.session.finish')}
        </button>
      </div>

      {finishing ? (
        <div className="kw-tile flex flex-col gap-3 border-accent">
          <p className="text-sm">{t('journal.rating.question')}</p>
          <ScalePicker
            large
            label={t('journal.rating.label')}
            low={t('journal.rating.low')}
            high={t('journal.rating.high')}
            value={null}
            onChange={(rating) => finish(rating)}
          />
          <button
            type="button"
            onClick={() => finish()}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('journal.rating.skip')}
          </button>
        </div>
      ) : null}

      {needsBodyWeight ? <BodyWeightPrompt sessionId={session.id} /> : null}

      {restEndsAt !== null ? (
        <RestTimer
          endsAt={restEndsAt}
          totalMs={restTotalMs}
          notice={
            restPush === 'on'
              ? t('training.rest.pushOn')
              : restPush === 'off'
                ? t('training.rest.pushOff')
                : restPush === 'failed'
                  ? t('training.rest.pushFailed')
                  : null
          }
          onCancel={stopRest}
        />
      ) : null}

      {supersetHint !== null ? (
        <p role="status" className="kw-tile text-sm">
          {t('training.session.supersetNext', { name: supersetHint })}
        </p>
      ) : null}

      {entries.length === 0 ? (
        <p className="kw-tile text-sm text-ink-muted">{t('training.session.noExercises')}</p>
      ) : null}

      <ol className="flex flex-col gap-3">
        {groupByBlock(entries).map((group) =>
          group.length === 1 ? (
            <li key={group[0]!.exercise.id}>
              <ExerciseBlock
                entry={group[0]!}
                session={session}
                sets={sets.filter((set) => set.exerciseId === group[0]!.exercise.id)}
                excluded={entries.map((item) => item.exercise.id)}
                onLogged={() => afterSet(group[0]!)}
              />
            </li>
          ) : (
            <li key={group[0]!.exercise.id} className="rounded-2xl border border-line p-2">
              <p className="kw-kicker px-1 pb-2">{t('training.session.superset')}</p>
              <ol className="flex flex-col gap-2">
                {group.map((entry) => (
                  <li key={entry.exercise.id}>
                    <ExerciseBlock
                      entry={entry}
                      session={session}
                      sets={sets.filter((set) => set.exerciseId === entry.exercise.id)}
                      excluded={entries.map((item) => item.exercise.id)}
                      onLogged={() => afterSet(entry)}
                    />
                  </li>
                ))}
              </ol>
            </li>
          ),
        )}
      </ol>

      <AddExercise
        excluded={entries.map((entry) => entry.exercise.id)}
        onPick={(id) => setExtraIds((current) => [...current, id])}
      />
    </section>
  );
}

/**
 * Die geplanten Übungen des Tages — mit wöchentlicher Rotation der
 * Zusatzübungen.
 *
 * Aus dem Fachkonzept: Ohne Rotation bekommt immer dieselbe Zusatzübung die
 * letzte, müdeste Position und wächst als Einzige nicht. Die Grundübungen
 * bleiben vorn und in ihrer Reihenfolge.
 */
async function planFor(session: LocalWorkoutSession): Promise<PlannedEntry[]> {
  if (!session.planDayId) return [];

  // Frisch gelesen, damit ein Tausch sofort sichtbar wird (Live-Abfrage).
  const current = await db.workout_sessions.get(session.id);
  const swaps = current?.swaps ?? {};
  const day = await db.plan_days.get(session.planDayId);
  const entries = await listDayExercises(session.planDayId, db);
  const plan = day ? await db.training_plans.get(day.planId) : undefined;

  const resolved: (Omit<PlannedEntry, 'block'> & {
    isCompound: boolean;
    supersetWithPrevious: boolean;
  })[] = [];
  for (const entry of entries) {
    const original = await getExercise(entry.exerciseId, db);
    if (!original) continue;
    // Getauscht wird nur für heute: Platz, Sätze und Wiederholungen bleiben
    // die des Plans, geloggt wird auf dem Ersatz. Fehlt der Ersatz (gelöscht),
    // gilt wieder die geplante Übung — nie ein leerer Platz.
    const swapId = swaps[entry.exerciseId];
    const replacement = swapId ? await getExercise(swapId, db) : undefined;
    const exercise = replacement ?? original;
    resolved.push({
      exercise,
      ...(replacement ? { swappedFrom: original } : {}),
      range: { min: entry.repMin, max: entry.repMax },
      sets: entry.sets,
      restSeconds: entry.restSeconds ?? exercise.restSeconds,
      planned: true,
      isCompound: original.isCompound,
      supersetWithPrevious: entry.supersetWithPrevious === true,
    });
  }

  // Wochengrenzen in der Zeitzone des Nutzers, nicht in UTC: Eine Einheit am
  // Sonntagabend gehört sonst schon in die nächste Woche und rotiert zu früh.
  const { timezone } = await readProfile(db);
  const weekIndex = plan?.startedAt
    ? weeksSince(new Date(plan.startedAt), new Date(session.startedAt), timezone)
    : 0;

  // Supersätze rotieren als Block (`orderPlanBlocks`), sonst risse die
  // Rotation sie auseinander.
  return orderPlanBlocks(resolved, weekIndex).flatMap((block, index) =>
    block.map(({ isCompound: _compound, supersetWithPrevious: _superset, ...entry }) => ({
      ...entry,
      block: index,
    })),
  );
}

function doneByExercise(sets: readonly LocalWorkoutSet[]): Map<string, number> {
  const done = new Map<string, number>();
  for (const set of sets) done.set(set.exerciseId, (done.get(set.exerciseId) ?? 0) + 1);
  return done;
}

/** Aufeinanderfolgende Einträge desselben Blocks zusammenfassen. */
function groupByBlock(entries: readonly PlannedEntry[]): PlannedEntry[][] {
  const groups: PlannedEntry[][] = [];
  for (const entry of entries) {
    const last = groups.at(-1);
    if (last && last[0]!.block === entry.block) last.push(entry);
    else groups.push([entry]);
  }
  return groups;
}

function BodyWeightPrompt({ sessionId }: { sessionId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const fieldId = useId();
  const [value, setValue] = useState('');

  useEffect(() => {
    void readProfile(db).then((profile) => {
      if (profile.weightKg !== null) setValue(String(profile.weightKg));
    });
  }, []);

  return (
    <form
      className="kw-tile border-warn"
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = Number(value.replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0) return;
        void updateSession(sessionId, { bodyWeightKg: parsed }, db);
      }}
    >
      <label htmlFor={fieldId} className="kw-label">
        {t('training.session.bodyWeightLabel')}
      </label>
      <p className="mt-1 text-xs text-ink-muted">{t('training.session.bodyWeightWhy')}</p>
      <div className="mt-2 flex items-end gap-2">
        <input
          id={fieldId}
          inputMode="decimal"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="kw-field w-28"
        />
        <button type="submit" className="kw-btn kw-btn-quiet kw-btn-small flex-1">
          {t('training.session.bodyWeightSave')}
        </button>
      </div>
    </form>
  );
}

function ExerciseBlock({
  entry,
  session,
  sets,
  excluded,
  onLogged,
}: {
  entry: PlannedEntry;
  session: LocalWorkoutSession;
  sets: LocalWorkoutSet[];
  /** Was schon in der Einheit steht — taugt nicht als Ersatz. */
  excluded: string[];
  onLogged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { exercise } = entry;
  const fields = setFieldsFor(exercise.trackingMode);
  const [open, setOpen] = useState(false);
  const [best, setBest] = useState<NewBest | null>(null);
  // Bestwerte (E13) — für Teens mit Training-Freigabe seit 28.10.2026 auch:
  // Das Hantelgewicht ist keine Körperzahl. Kinder darunter: keine.
  const recordsInScope = useAchievementScope().records;
  const achievements =
    useLiveQuery(async () => (await readProfile(db)).achievements, [], false) && recordsInScope;

  const last = useLiveQuery(
    () => lastSetsOfExercise(exercise.id, db, session.id),
    [exercise.id, session.id],
    null,
  );

  const suggestion = useLiveQuery(
    () => suggestionForExercise(exercise, entry.range, db, session.id),
    [exercise.id, entry.range.min, entry.range.max, session.id],
    null,
  ) as ProgressionSuggestion | null;

  const parent = useLiveQuery(
    async () => (exercise.parentId ? await getExercise(exercise.parentId, db) : null),
    [exercise.parentId],
    null,
  );

  const guidance = guidanceFor(exercise, parent);

  return (
    <article className="kw-hero">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{exercise.name}</h3>
          {entry.swappedFrom ? (
            <p className="text-xs text-ink-muted">
              {t('training.session.swappedFrom', { name: entry.swappedFrom.name })}
            </p>
          ) : null}
          <p className="kw-kicker mt-0.5">
            {fields.reps
              ? t('training.session.target', {
                  sets: entry.sets,
                  min: entry.range.min,
                  max: entry.range.max,
                })
              : t('training.session.targetSetsOnly', { sets: entry.sets })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('training.session.details')}
        </button>
      </header>

      {open ? (
        <div className="mt-3 flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3 text-sm">
          <ExerciseArtFigure catalogKey={exercise.catalogKey} name={exercise.name} small />
          {exercise.setupNotes ? (
            <p>
              <span className="text-ink-muted">{t('training.exercise.setupNotes')}: </span>
              {exercise.setupNotes}
            </p>
          ) : null}

          {guidance.cues.length > 0 ? (
            <div>
              <p className="text-xs text-ink-muted">
                {guidance.cuesFrom
                  ? t('training.exercise.inheritedFrom', { name: guidance.cuesFrom })
                  : t('training.exercise.cues')}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {guidance.cues.map((cue) => (
                  <li key={cue}>{cue}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ink-muted">{t('training.exercise.cuesDisclaimer')}</p>
            </div>
          ) : null}

          {guidance.videos.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {guidance.videos.map((video) => (
                <li key={video.url}>
                  <a
                    href={videoUrlWithStart(video)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent underline"
                  >
                    {video.label} · {videoHost(video.url) ?? t('training.exercise.unknownHost')}
                  </a>
                </li>
              ))}
              <li className="text-xs text-ink-muted">{t('training.exercise.videoNeedsNetwork')}</li>
            </ul>
          ) : (
            // Ein leerer Platz sähe aus wie „kein Video nötig".
            <p className="text-xs text-ink-muted">{t('training.exercise.noVideo')}</p>
          )}

          {entry.planned ? (
            <SwapToday entry={entry} sessionId={session.id} excluded={excluded} />
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-1 text-sm">
        {last ? (
          <p className="text-ink-muted">
            {t('training.session.lastTime', {
              date: new Date(last.startedAt).toLocaleDateString(),
            })}{' '}
            <span className="text-ink">{summarizeSets(last.sets, exercise)}</span>
          </p>
        ) : (
          <p className="text-ink-muted">{t('training.session.noHistory')}</p>
        )}

        {suggestion ? (
          <p className={suggestion.reason === 'increase' ? 'text-accent' : 'text-ink-muted'}>
            {t(`training.session.suggestion.${suggestion.reason}`, {
              weight: formatNumber(suggestion.weightKg),
              increment: formatNumber(suggestion.incrementKg),
            })}
          </p>
        ) : null}
      </div>

      {sets.length > 0 ? (
        <ol className="mt-3 flex flex-col">
          {sets.map((set, index) => (
            <li
              key={set.id}
              className="flex items-center justify-between gap-2 border-t border-line py-2"
            >
              <span className="flex items-baseline gap-2.5">
                <span className="kw-kicker">
                  {set.isWarmup ? t('training.session.warmup') : `${index + 1}`}
                </span>
                <span className="kw-figure text-lg">{describeSet(set, exercise)}</span>
              </span>
              <button
                type="button"
                onClick={() => void deleteSet(set.id, db)}
                aria-label={t('training.session.deleteSetNamed', { index: index + 1 })}
                className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
              >
                {t('training.session.deleteSet')}
              </button>
            </li>
          ))}
        </ol>
      ) : null}

      {best ? (
        // Ein Bestwert wird gefeiert, wo er passiert — direkt am Satz.
        <p
          role="status"
          className="kw-pop mt-3 rounded-xl border border-good p-3 text-sm text-good"
        >
          <span className="font-semibold">{t('rings.newBest')}</span>{' '}
          {t('rings.newBestValue', {
            kg: formatNumber(Math.round(best.kg * 2) / 2),
            gain: formatNumber(Math.round((best.kg - best.previousKg) * 2) / 2),
          })}
        </p>
      ) : null}

      <SetForm
        exercise={exercise}
        suggestion={suggestion}
        lastSets={last?.sets ?? []}
        onSubmit={(input) => {
          void logSetWithBest(session.id, exercise.id, input, db).then(({ newBest }) => {
            if (newBest && achievements) setBest(newBest);
            onLogged();
          });
        }}
      />
    </article>
  );
}

function SetForm({
  exercise,
  suggestion,
  lastSets,
  onSubmit,
}: {
  exercise: LocalExercise;
  suggestion: ProgressionSuggestion | null;
  lastSets: LocalWorkoutSet[];
  onSubmit: (input: {
    weightKg?: number | null;
    reps?: number | null;
    seconds?: number | null;
    distanceM?: number | null;
    isWarmup?: boolean;
    side?: 'left' | 'right' | null;
  }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const fields = setFieldsFor(exercise.trackingMode);
  const ids = {
    weight: useId(),
    reps: useId(),
    seconds: useId(),
    distance: useId(),
  };

  const previous = lastSets.find((set) => !set.isWarmup);
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [seconds, setSeconds] = useState('');
  const [distance, setDistance] = useState('');
  const [warmup, setWarmup] = useState(false);
  const [side, setSide] = useState<'left' | 'right'>('left');

  // Vorbelegen: Vorschlag schlägt letzten Wert. Beides ist besser als ein leeres
  // Feld, in das man mit kalten Fingern tippt.
  //
  // Die Abhängigkeiten sind bewusst kurz gehalten: Es soll **einmal** vorbelegt
  // werden, wenn sich Übung oder Vorschlag ändern — nicht bei jedem Tastendruck,
  // sonst überschreibt die App die Eingabe, während sie entsteht.
  const suggestedWeight = suggestion?.weightKg ?? null;
  const previousId = previous?.id ?? null;
  useEffect(() => {
    if (suggestedWeight !== null && fields.weight) setWeight(formatNumber(suggestedWeight));
    else if (previous?.weightKg !== null && previous?.weightKg !== undefined && fields.weight) {
      setWeight(formatNumber(previous.weightKg));
    }
    if (previous?.reps !== null && previous?.reps !== undefined && fields.reps) {
      setReps(String(previous.reps));
    }
    if (previous?.seconds !== null && previous?.seconds !== undefined && fields.seconds) {
      setSeconds(String(previous.seconds));
    }
    if (previous?.distanceM !== null && previous?.distanceM !== undefined && fields.distance) {
      setDistance(String(previous.distanceM));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedWeight, previousId]);

  return (
    <form
      className="mt-4 flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          weightKg: fields.weight ? toNumber(weight) : null,
          reps: fields.reps ? toNumber(reps) : null,
          seconds: fields.seconds ? toNumber(seconds) : null,
          distanceM: fields.distance ? toNumber(distance) : null,
          isWarmup: warmup,
          side: exercise.isUnilateral ? side : null,
        });
        setWarmup(false);
      }}
    >
      {fields.weight ? (
        <Stepper
          id={ids.weight}
          label={
            exercise.trackingMode === 'weightedBodyweight'
              ? t('training.set.addedWeight')
              : t('training.set.weight')
          }
          value={weight}
          onChange={setWeight}
          // Die Stufe der Übung, nicht eine erfundene: An der Langhantel sind
          // 2,5 kg der kleinste Sprung, an der Maschine oft 5.
          step={exercise.incrementKg}
        />
      ) : null}
      {fields.reps ? (
        <Stepper
          id={ids.reps}
          label={t('training.set.reps')}
          value={reps}
          onChange={setReps}
          step={1}
        />
      ) : null}
      {fields.seconds ? (
        <Stepper
          id={ids.seconds}
          label={t('training.set.seconds')}
          value={seconds}
          onChange={setSeconds}
          step={5}
        />
      ) : null}
      {fields.distance ? (
        <Stepper
          id={ids.distance}
          label={t('training.set.distance')}
          value={distance}
          onChange={setDistance}
          step={100}
        />
      ) : null}

      {exercise.isUnilateral ? (
        <div className="flex flex-col gap-1.5">
          <span className="kw-label" id={`${ids.weight}-side`}>
            {t('training.set.side')}
          </span>
          <div className="flex gap-2" role="group" aria-labelledby={`${ids.weight}-side`}>
            {(['left', 'right'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={side === option}
                onClick={() => setSide(option)}
                className={`kw-btn kw-btn-small flex-1 ${
                  side === option ? '' : 'kw-btn-quiet text-ink'
                }`}
              >
                {t(`training.set.${option}`)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <label className="flex items-center gap-3 py-1 text-sm text-ink-muted">
        <input
          type="checkbox"
          checked={warmup}
          onChange={(event) => setWarmup(event.target.checked)}
          className="size-6 accent-accent"
        />
        {t('training.set.warmup')}
      </label>

      <button type="submit" className="kw-btn w-full">
        {t('training.set.log')}
      </button>
    </form>
  );
}

/**
 * Ein Wert, zwei Daumenflächen — und trotzdem tippbar.
 *
 * Zwischen zwei Sätzen sind die Finger nass und der Blick kurz; ein Zahlenfeld
 * mit eingeblendeter Tastatur kostet drei Griffe. Plus und Minus springen um die
 * Stufe der Übung, und weil der Wert aus dem Vorschlag oder dem letzten Satz
 * vorbelegt ist, reicht meist ein Tipp.
 *
 * Die Zahl bleibt ein Eingabefeld: Wer von 20 auf 60 kg wechselt, soll das nicht
 * ertippen müssen. Ein reiner Stepper wäre für den Normalfall schneller und für
 * den Ausnahmefall unbrauchbar.
 */
function Stepper({
  id,
  label,
  value,
  onChange,
  step,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  step: number;
}): React.JSX.Element {
  const { t } = useTranslation();

  const adjust = (delta: number) => {
    const current = toNumber(value) ?? 0;
    // Auf zwei Stellen runden: 72,5 + 2,5 ergibt in Gleitkomma sonst gern
    // 75.00000000000001, und das stünde dann so da.
    const next = Math.max(0, Math.round((current + delta) * 100) / 100);
    onChange(formatNumber(next));
  };

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => adjust(-step)}
        aria-label={t('training.set.less', { field: label })}
        className="kw-step"
      >
        −
      </button>

      <div className="min-w-0 flex-1 text-center">
        <input
          id={id}
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          // Beim ersten Mal gibt es weder Vorschlag noch letzten Wert. Ein leeres
          // Feld zwischen zwei Knöpfen sieht aus, als sei etwas kaputt — der
          // Strich sagt „noch keine Angabe" und ist nicht die Behauptung „0".
          placeholder="—"
          className="kw-figure h-12 w-full border-0 bg-transparent text-center text-4xl"
        />
        <label htmlFor={id} className="kw-label block">
          {label}
        </label>
      </div>

      <button
        type="button"
        onClick={() => adjust(step)}
        aria-label={t('training.set.more', { field: label })}
        className="kw-step"
      >
        +
      </button>
    </div>
  );
}

/**
 * „Nur heute tauschen“ — Gerät belegt, Schulter zwickt. Ähnliche Übungen
 * (gleicher Grundtyp) stehen oben; der Plan bleibt, wie er ist.
 */
function SwapToday({
  entry,
  sessionId,
  excluded,
}: {
  entry: PlannedEntry;
  sessionId: string;
  excluded: string[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const selectId = useId();
  const exercises = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);
  const planned = entry.swappedFrom ?? entry.exercise;
  const rootId = planned.parentId ?? planned.id;
  const available = exercises.filter(
    (candidate) => candidate.id !== planned.id && !excluded.includes(candidate.id),
  );
  const siblings = available.filter(
    (candidate) => candidate.id === rootId || candidate.parentId === rootId,
  );
  const others = available.filter((candidate) => !siblings.includes(candidate));

  return (
    <div className="flex flex-col gap-1.5 border-t border-line pt-2">
      <label htmlFor={selectId} className="kw-label">
        {t('training.session.swapToday')}
      </label>
      <p className="text-xs text-ink-muted">{t('training.session.swapTodayWhy')}</p>
      <select
        id={selectId}
        defaultValue=""
        onChange={(event) => {
          if (event.target.value) void swapForToday(sessionId, planned.id, event.target.value, db);
          event.target.value = '';
        }}
        className="kw-field"
      >
        <option value="">{t('training.session.swapTodayPlaceholder')}</option>
        {siblings.length > 0 ? (
          <optgroup label={t('training.plans.sameFamily')}>
            {siblings.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {shortLabel(candidate)}
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label={t('training.plans.allExercises')}>
          {others.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </optgroup>
      </select>
      {entry.swappedFrom ? (
        <button
          type="button"
          onClick={() => void swapForToday(sessionId, planned.id, null, db)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('training.session.swapBack', { name: planned.name })}
        </button>
      ) : null}
    </div>
  );
}

function AddExercise({
  excluded,
  onPick,
}: {
  excluded: string[];
  onPick: (id: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selectId = useId();
  const exercises = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);
  const recentIds = useLiveQuery(() => recentExerciseIds(8, db), [], [] as string[]);
  const available = exercises.filter((exercise) => !excluded.includes(exercise.id));
  const recent = recentIds
    .map((id) => available.find((exercise) => exercise.id === id))
    .filter((exercise): exercise is LocalExercise => exercise !== undefined)
    .slice(0, 6);

  return (
    <div className="kw-tile flex flex-col gap-3">
      {recent.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="kw-label">{t('training.session.recent')}</p>
          <ul className="flex flex-wrap gap-2">
            {recent.map((exercise) => (
              <li key={exercise.id}>
                <button type="button" onClick={() => onPick(exercise.id)} className="kw-toggle">
                  {exercise.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={selectId} className="kw-label">
          {t('training.session.addExercise')}
        </label>
        <select
          id={selectId}
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) onPick(event.target.value);
            event.target.value = '';
          }}
          className="kw-field"
        >
          <option value="">{t('training.session.addExercisePlaceholder')}</option>
          {available.map((exercise) => (
            <option key={exercise.id} value={exercise.id}>
              {exercise.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// --- Anzeige-Hilfen ----------------------------------------------------------

function toNumber(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
}

export function describeSet(set: LocalWorkoutSet, exercise: LocalExercise): string {
  const parts: string[] = [];
  const fields = setFieldsFor(exercise.trackingMode);
  if (fields.weight && set.weightKg !== null) parts.push(`${formatNumber(set.weightKg)} kg`);
  if (fields.reps && set.reps !== null) parts.push(`${set.reps}×`);
  if (fields.seconds && set.seconds !== null) parts.push(`${set.seconds} s`);
  if (fields.distance && set.distanceM !== null)
    parts.push(`${formatNumber(set.distanceM / 1000)} km`);
  if (set.side) parts.push(set.side === 'left' ? 'L' : 'R');
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function summarizeSets(sets: readonly LocalWorkoutSet[], exercise: LocalExercise): string {
  const working = sets.filter((set) => !set.isWarmup);
  if (working.length === 0) return '—';
  return working.map((set) => describeSet(set, exercise)).join(' | ');
}
